import { useEffect, useState } from 'react';
import { App, Button, Modal } from 'antd';
import { api, type ApiError } from '@/lib/api';
import { PDF_VIEWER_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3b §5] THE ONE PDF READER IN THIS APP. EVERY ATTACHMENT OPENS HERE.
 *
 *  ⚠️ CLICKING AN ATTACHMENT USED TO **DOWNLOAD** IT, WHICH IS THE WHOLE OF WHY THIS EXISTS.
 *  `AttachmentList` built an `<a download>` and clicked it, so the only way to read a PA that
 *  had been attached to an order was to save it and leave the app. There is now one modal, and
 *  every surface that shows a PDF opens it — the arrangement builder, the arrangement view's
 *  file menu, and the uploaded-order library.
 *
 *  ⚠️ IT TAKES A `path`, NOT A BLOB, so a caller never handles bytes. Two routes serve PDFs on
 *  different mounts (`/arrangements/:id/attachments/:attachmentId` and
 *  `/uploaded-orders/:id/file`) and both answer the same three headers; a caller that had to
 *  fetch first would be a second place the blob lifecycle could go wrong.
 *
 *  ⚠️ THE OBJECT URL IS REVOKED WHEN THE MODAL CLOSES, NOT AT ONCE. `AttachmentList`'s download
 *  path revoked immediately and was right to — `anchor.click()` has already read the URL by the
 *  time it returns. An `<iframe>` reads it for as long as it is mounted, so revoking early
 *  leaves a blank frame with no error.
 *
 *  ⚠️ AND THE DOWNLOAD BUTTON IS UNCONDITIONAL, NOT A FALLBACK THE CODE CHOOSES. A browser with
 *  its built-in PDF viewer disabled renders an EMPTY `<iframe>` and fires **no error event** —
 *  there is nothing to detect, so there is nothing to fall back on automatically. The escape
 *  hatch is always on screen.
 *
 *  ⚠️ `vw`/`vh` ARE CORRECT HERE AND NOWHERE ELSE IN `web/`. §8 requires CONTAINER queries
 *  because `<main>` is 304px narrower than the window; a Modal is portalled to `document.body`,
 *  outside `<main>`, and is anchored to the viewport — so the viewport is what it must measure.
 *
 *  ⚠️ THE `cancelled` GUARD IS ON **BOTH** PROMISE ARMS. `api.getBlob` takes no signal, so
 *  closing cannot cancel the request; without the guard on the `catch`, dismissing a slow or
 *  failing view raises a toast seconds later about a file the operator has already walked away
 *  from, with no dialog on screen to attach it to.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type PdfSource = {
  /** The API path that serves the bytes. The caller owns the route; this owns the reading. */
  readonly path: string;
  /** What the browser saves it as, and what the dialog is titled when there is nothing better. */
  readonly filename: string;
  /** The operator's own name for it, where one exists — an uploaded order has a title. */
  readonly title?: string;
};

export const PdfViewerModal = ({
  source,
  onClose,
}: {
  source: PdfSource | null;
  onClose: () => void;
}): React.ReactElement => {
  const { message } = App.useApp();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (source === null) return undefined;
    let objectUrl: string | null = null;
    let cancelled = false;

    void api
      .getBlob(source.path)
      .then(({ blob }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        void message.error((error as ApiError).message);
      });

    return () => {
      cancelled = true;
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [source, message]);

  const heading = source?.title ?? source?.filename ?? PDF_VIEWER_COPY.title;

  return (
    <Modal
      open={source !== null}
      title={heading}
      onCancel={onClose}
      /* ⚠️ NEAR-FULL-SCREEN, WHICH IS THE POINT — an A4 order sheet at half width is unreadable. */
      width="92vw"
      style={{ top: 24, maxWidth: 1400 }}
      footer={
        <Button
          type="primary"
          disabled={url === null}
          data-testid="pdf-download"
          onClick={() => {
            if (url === null || source === null) return;
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = source.filename;
            anchor.click();
          }}
        >
          {PDF_VIEWER_COPY.download}
        </Button>
      }
      data-testid="pdf-viewer-modal"
    >
      <p className="mb-2 text-xs text-muted-foreground">{PDF_VIEWER_COPY.note}</p>
      {url === null ? (
        <p className="text-sm text-muted-foreground" data-testid="pdf-loading">
          {PDF_VIEWER_COPY.loading}
        </p>
      ) : (
        <iframe src={url} title={heading} className="h-[78vh] w-full border-0" data-testid="pdf-frame" />
      )}
    </Modal>
  );
};
